import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useInvertedCursor } from '../../hooks/useInvertedCursor';
import { BackgroundBeams } from '../../components/ui/background-beams';
import { BentoGrid, BentoGridItem } from '../../components/ui/bento-grid';
import { InfiniteMovingCards } from '../../components/ui/infinite-moving-cards';
import { SEO } from '../../components/SEO';
import { CameraIcon, PaintBrushIcon, SparklesIcon, RocketLaunchIcon, BrandActivationIcon, CorporateGalaIcon, TradeShowIcon, WeddingPartyIcon } from '../../components/Icons';
import { MdPlayArrow } from 'react-icons/md';
import './AiPhotobooth.css';

const FadeInSection = ({ children, className = '', delay = '' }) => {
    const [ref, isVisible] = useIntersectionObserver({ triggerOnce: true, threshold: 0.1 });
    return (
        <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className} ${delay}`}>
            {children}
        </div>
    );
};

const AiPhotobooth = () => {
    useInvertedCursor();
    
    useEffect(() => {
        // Scroll to top on mount
        window.scrollTo(0, 0);
        
        // Add structured data for AI Photo Booth page
        const serviceSchema = {
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "AI Photo Booth",
            "provider": {
                "@type": "Organization",
                "name": "Mad Over Technology",
                "url": "https://madovertech.io"
            },
            "areaServed": {
                "@type": "Country",
                "name": "India"
            },
            "description": "Transform every selfie into a shareable AI portrait, instantly & branded. AI Photo Booth for events, exhibitions, retail activations, and online UGC campaigns.",
            "offers": {
                "@type": "Offer",
                "url": "https://madovertech.io/ai-photobooth"
            }
        };

        let schemaScript = document.querySelector('script[data-schema="ai-photobooth"]');
        if (!schemaScript) {
            schemaScript = document.createElement('script');
            schemaScript.setAttribute('type', 'application/ld+json');
            schemaScript.setAttribute('data-schema', 'ai-photobooth');
            document.head.appendChild(schemaScript);
        }
        schemaScript.textContent = JSON.stringify(serviceSchema);
    }, []);

    return (
        <>
            <SEO 
                title="AI Photo Booth Experience | Mad Over Technology"
                description="Transform every selfie into a shareable AI portrait, instantly & branded. AI Photo Booth for events, exhibitions, retail activations. Custom themes, real-time transformation, and instant sharing."
                keywords="AI Photo Booth, AI Portrait, Event Technology, Brand Activation, AI Transformation, Photo Booth Rental, Event Photography, AI Content, Delhi, Mumbai, Bangalore"
                url="/ai-photobooth"
                image="/ai-photobooth/hero.png"
            />
        <main className="ai-photobooth relative">
            {/* Hero Section */}
            <section className="hero-section relative overflow-hidden flex items-center">
                <BackgroundBeams className="z-0" />
                <div className="container mx-auto px-4 relative z-10 grid lg:grid-cols-2 gap-12 items-center">
                    <div className="hero-text text-left">
                        <h1 className="hero-title text-5xl md:text-7xl font-bold mb-6 text-neutral-900 dark:text-black">
                            AI Photo Booth Experience
                        </h1>
                        <p className="hero-subtitle text-lg md:text-xl text-neutral-600 dark:text-neutral-400 mb-8 max-w-lg mx-0">
                            Transform every selfie into a shareable AI portrait, instantly & branded.
                        </p>
                        <div className="flex gap-4 justify-start flex-wrap">
                            <a href="https://photobooth.madovertech.io" target="_blank" rel="noopener noreferrer" className="cta-button">
                                <MdPlayArrow className="demo-icon" size={22} style={{ color: '#ffffff' }} />
                                <span>Live Demo</span>
                            </a>
                            <a href="https://wa.me/919711699861?text=Hi%2C%20I%27m%20interested%20in%20the%20AI%20Photo%20Booth%20Experience" target="_blank" rel="noopener noreferrer" className="cta-button whatsapp-button">
                                <svg className="whatsapp-icon" width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                </svg>
                                <span>WhatsApp Us</span>
                            </a>
                        </div>
                    </div>
                    <div className="hero-visual">
                        <video
                            src="/ai-photobooth/mot-photobooth.mp4"
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="w-full rounded-2xl shadow-2xl border border-neutral-200 dark:border-neutral-800"
                            aria-label="AI Photo Booth demonstration video showing real-time image transformation"
                        />
                    </div>
                </div>
            </section>

            {/* Trusted By Section */}
            <section className="py-20 bg-white overflow-hidden">
                <FadeInSection>
                    <h2 className="section-title trusted-by-title">Trusted by Industry Leaders</h2>
                </FadeInSection>
                <InfiniteMovingCards
                    items={[
                        { logo: "/logos/pegafest.png", name: "PegaFest 25", quote: "PegaFest 25" },
                        { logo: "/logos/baliyatra.png", name: "Baliyatra", quote: "Baliyatra" },
                        { logo: "/logos/vidhan-sabha.png", name: "Vidhan Sabha", quote: "Vidhan Sabha" },
                        { logo: "/logos/ministry-power.svg", name: "Ministry of Power", quote: "Ministry of Power" },
                        { logo: "/logos/epfo.png", name: "EPFO", quote: "EPFO" },
                    ]}
                    direction="right"
                    speed="slow"
                    className="bg-transparent"
                />
            </section>

            {/* How It Works */}
            <section className="how-it-works">
                <FadeInSection>
                    <h2 className="section-title">How It Works</h2>
                </FadeInSection>
                <div className="steps-grid">
                    {[
                        { name: 'Capture', Icon: CameraIcon },
                        { name: 'Select Theme', Icon: PaintBrushIcon },
                        { name: 'Transform', Icon: SparklesIcon },
                        { name: 'Customize & Share', Icon: RocketLaunchIcon }
                    ].map((step, index) => (
                        <FadeInSection key={step.name} className="step-card" delay={`stagger-${index + 1}`}>
                            <div className="step-number">0{index + 1}</div>
                            <div className="step-content">
                                <div style={{ marginBottom: '1rem' }}><step.Icon className="w-10 h-10" /></div>
                                <h3 className="step-title">{step.name}</h3>
                                <p>{
                                    index === 0 ? "Tap 'Start' to capture your photo from any device or kiosk." :
                                        index === 1 ? "Choose from our expertly crafted themes or custom brand templates." :
                                            index === 2 ? "Watch as AI instantly transforms your photo. Not satisfied? Hit 'Regenerate'." :
                                                "Apply branded overlays, then share via QR/Email or print instantly."
                                }</p>
                            </div>
                        </FadeInSection>
                    ))}
                </div>
            </section>

            {/* Use Cases */}
            <section className="use-cases-section">
                <FadeInSection>
                    <h2 className="section-title">Perfect For Every Occasion</h2>
                </FadeInSection>
                <div className="steps-grid">
                    {[
                        { title: 'Brand Activations', desc: 'Make your brand the hero. Custom prompts ensure every image aligns with your campaign.', Icon: BrandActivationIcon, image: '/ai-photobooth/mot-brandactiivation.jpg' },
                        { title: 'Corporate Galas', desc: 'From "Roaring 20s" to "Cyberpunk Future", match the event theme perfectly.', Icon: CorporateGalaIcon, image: '/ai-photobooth/mot-corporategala.jpg' },
                        { title: 'Trade Shows', desc: 'Stop traffic with a "Magazine Cover" experience that puts attendees on the front page.', Icon: TradeShowIcon, image: '/ai-photobooth/mot-tradeshow.jpg' },
                        { title: 'Weddings & Parties', desc: 'Create unforgettable keepsakes for your special guests with a touch of AI magic.', Icon: WeddingPartyIcon, image: '/ai-photobooth/mot-wedding.jpg' }
                    ].map((useCase, index) => (
                        <FadeInSection key={useCase.title} className="use-case-card" delay={`stagger-${index + 1}`}>
                            <div className="use-case-image-wrapper">
                                <img src={useCase.image} alt={useCase.title} className="use-case-image" loading="lazy" />
                                <div className="use-case-icon-overlay">
                                    <useCase.Icon className="w-8 h-8" />
                                </div>
                            </div>
                            <div className="use-case-content">
                                <h3>{useCase.title}</h3>
                                <p>{useCase.desc}</p>
                            </div>
                        </FadeInSection>
                    ))}
                </div>
            </section>

            {/* Gallery */}
            <section className="gallery-section">
                <FadeInSection>
                    <h2 className="section-title">Style Gallery</h2>
                    <p className="gallery-subtitle">Explore our diverse collection of AI-powered photo styles. Each style transforms your photos with unique artistic flair and professional quality.</p>
                </FadeInSection>
                <div className="gallery-grid">
                    {[
                        { id: 1, name: 'Vidhan Sabha', img: '/ai-photobooth/pb-mot1.png' },
                        { id: 2, name: 'Baliyatra', img: '/ai-photobooth/pb-mot2.png' },
                        { id: 3, name: 'Ministry of Power', img: '/ai-photobooth/pb-mot3.png' },
                        { id: 4, name: 'Marvel Photobooth', img: '/ai-photobooth/pb-mot4.png' }
                    ].map((style, i) => (
                        <FadeInSection key={style.id} className="gallery-item" delay={`stagger-${i + 1}`}>
                            <img src={style.img} alt={`${style.name} AI Photo Booth Style Example`} className="gallery-image" loading="lazy" />
                            <div className="gallery-overlay">
                                <span>{style.name}</span>
                            </div>
                        </FadeInSection>
                    ))}
                </div>
            </section>

            {/* CTA / Contact */}
            <section id="contact" className="cta-section">
                <FadeInSection className="cta-content">
                    <h2 className="cta-title">Ready to Elevate Your Event?</h2>
                    <p className="cta-subtitle">Try the live demo and see the magic in action.</p>
                    <div className="cta-buttons">
                        <a href="https://wa.me/919711699861?text=Hi%2C%20I%27m%20interested%20in%20the%20AI%20Photo%20Booth%20Experience" target="_blank" rel="noopener noreferrer" className="cta-button whatsapp-button">
                            <svg className="whatsapp-icon" width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                            <span>Get Started</span>
                        </a>
                        <a href="https://photobooth.madovertech.io" target="_blank" rel="noopener noreferrer" className="cta-button secondary">Try on Device</a>
                    </div>
                </FadeInSection>
            </section>

            {/* Footer */}
            <footer className="footer">
                <div className="footer-links">
                    <Link to="/">Home</Link>
                    <a href="https://nexasync.io" target="_blank" rel="noopener noreferrer">NexaSync</a>
                    <Link to="/terms-of-service">Terms of Service</Link>
                    <Link to="/privacy-policy">Privacy Policy</Link>
                    <Link to="/refund-policy">Refund Policy</Link>
                    <a href="https://www.instagram.com/madovertechnology" target="_blank" rel="noopener noreferrer">Instagram</a>
                    <a href="https://wa.me/919711699861?text=Hi%2C%20I%27m%20interested%20in%20learning%20more%20about%20your%20services" target="_blank" rel="noopener noreferrer">Contact</a>
                </div>
                <div className="footer-locations">
                    <span>DELHI | BENGALORE | MUMBAI</span>
                </div>
                <div className="footer-contact">
                    <a href="mailto:hello@madovertech.io" className="footer-email">hello@madovertech.io</a>
                    <span className="footer-separator">|</span>
                    <a href="tel:+919711689861" className="footer-phone">+91 9711689861</a>
                </div>
                <p className="footer-copyright">© 2025 Mad Over Technology. All rights reserved.</p>
            </footer>
        </main>
        </>
    );
};

export default AiPhotobooth;
